/**
 * DOM指标空状态监控系统 - 简化类型定义
 */

// =============================================================================
// 简化配置类型
// =============================================================================

/**
 * 简化的监控配置
 */
export interface ISimpleMonitorConfig {
  /** 全局开关 */
  enabled: boolean;
  /** 检测延迟时间（毫秒） */
  detectionDelay: number;
  /** 防重复上报间隔（毫秒） */
  reportInterval: number;
}

/**
 * 预设选择器配置 - 父级容器 + 双选择器模式
 */
export interface IPresetSelector {
  /** 父级容器选择器 */
  containerSelector: string;
  /** 指标元素选择器（相对于父级容器） */
  metricSelector: string;
  /** 指标名称选择器（相对于父级容器） */
  nameSelector: string;
  /** 默认名称前缀（名称提取失败时使用） */
  defaultName?: string;
  /** 业务数据（可选） */
  businessData?: Record<string, any>;
  /** 页面路径匹配规则（可选，支持字符串、正则表达式或数组） */
  pathPattern?: string | RegExp | string[];
  /** 排除路径规则（可选，支持字符串、正则表达式或数组） */
  excludePaths?: string | RegExp | string[];
}

/**
 * 简化的DOM扫描器选项
 */
export interface ISimpleDOMScannerOptions {
  /** 标记元素选择器 */
  markedSelector: string;      // '[data-metrics-monitor]'
  /** 忽略元素选择器 */
  ignoreSelector: string;      // '[data-metrics-ignore-empty]'
  /** 预设CSS选择器列表 */
  presetSelectors?: IPresetSelector[];
}

// =============================================================================
// 元素配置类型
// =============================================================================

/**
 * 元素配置信息
 */
export interface IElementConfig {
  /** 元素唯一标识 */
  elementId: string;
  /** 元素标识名称 */
  name: string;
  /** CSS选择器路径 */
  selector: string;
  /** 业务数据（从data-*属性提取） */
  businessData?: Record<string, any>;
  /** 元素的DOM节点引用 */
  element: Element;
}

/**
 * 增强的元素配置信息（包含检测到的空状态详细信息）
 */
export interface IEnhancedElementConfig extends IElementConfig {
  /** 空状态的具体原因描述 */
  emptyReason: string;
  /** 检测到空状态的时间戳 */
  detectedAt: number;
  /** HTML标签名 */
  tagName: string;
  /** CSS类名（可选） */
  className?: string;
  /** 元素的文本内容（前100个字符，可选） */
  textContent?: string;
  /** 子元素数量 */
  childrenCount: number;
}

// =============================================================================
// 检测结果类型
// =============================================================================

/**
 * 空状态检测结果
 */
export interface IDetectionResult {
  /** 是否为空状态 */
  isEmpty: boolean;
  /** 空状态类型 */
  emptyType: TEmptyStateType;
  /** 检测原因 */
  reason?: string;
}

/**
 * 空状态类型
 */
export type TEmptyStateType = 
  | 'no_content'      // 无文本内容
  | 'empty_class'     // 包含空状态类名
  | 'no_children'     // 无有效子元素
  | 'has_content';    // 有内容

// =============================================================================
// 上报数据类型
// =============================================================================

/**
 * 简化的空指标上报数据
 */
export interface ISimpleMetricsReport {
  /** 基础信息 */
  reportId: string;
  reportType: 'metrics_empty';
  timestamp: number;
  
  /** 页面信息 */
  page: {
    url: string;
    path: string;
    title: string;
  };
  
  /** 元素统计信息 */
  statistics: {
    totalElements: number;        // 页面总打标元素数
    emptyElements: number;        // 空状态元素数
    emptyRatio: number;          // 空元素比例 (0-1)
  };
  
  /** 空指标信息 */
  emptyMetrics: Array<{
    elementId: string;
    elementName: string;
    selector: string;
    // emptyType: TEmptyStateType;
    emptyReason: string;                    // 空状态的具体原因描述
    detectedAt: number;                     // 检测到空状态的时间戳
    tagName: string;                        // HTML标签名
    className?: string;                     // CSS类名
    textContent?: string;                   // 元素的文本内容（前100个字符）
    childrenCount: number;                  // 子元素数量
    businessData?: Record<string, any>;     // 业务数据
  }>;
  
  /** 环境信息 */
  environment: {
    userAgent: string;
    viewport: { width: number; height: number };
  };
}

// =============================================================================
// 扫描结果类型
// =============================================================================

/**
 * DOM扫描结果
 */
export interface IScanResult {
  /** 扫描到的元素配置 */
  elements: IElementConfig[];
  /** 扫描时间 */
  scanTime: number;
  /** 元素总数 */
  totalCount: number;
}

// =============================================================================
// 路径匹配相关类型
// =============================================================================

/**
 * 路径匹配模式类型
 */
export type TPathPattern = string | RegExp | string[];

/**
 * 路径匹配结果
 */
export interface IPathMatchResult {
  /** 是否匹配 */
  matched: boolean;
  /** 匹配的模式 */
  matchedPattern?: string | RegExp;
  /** 匹配的路径 */
  matchedPath: string;
}

// =============================================================================
// 常量定义
// =============================================================================

/** 默认配置 */
export const SIMPLE_DEFAULT_CONFIG: ISimpleMonitorConfig = {
  enabled: true,
  detectionDelay: 1000,
  reportInterval: 10 * 60 * 1000
};

/** 默认扫描器选项 */
export const SIMPLE_DEFAULT_SCANNER_OPTIONS: ISimpleDOMScannerOptions = {
  markedSelector: '[data-metrics-monitor]',
  ignoreSelector: '[data-metrics-ignore-empty]',
  presetSelectors: [
    // mobile 日报卡片
    {
      containerSelector: '.dbd-a-card-container',
      metricSelector: '.overlay-card-body-num',
      nameSelector: '.overlay-card-title .overlay-card-text',
      defaultName: '指标卡片',
      businessData: { type: 'dashboard-metric-card', module: 'dashboard' },
      pathPattern: ['/overview/']
    },
    // studio 首页卡片  
    {
      containerSelector: '.dbd-metrics-card-wrap.dbh-studio-home-rank-card',
      metricSelector: '.dbd-metric-card-value',
      nameSelector: '.dbd-metrics-card-wrap__title__text',
      defaultName: '指标卡片',
      businessData: { type: 'studio-metric-card', module: 'studio' },
      pathPattern: ['/studio/']
    },
    // pc 日报卡片
    {
      containerSelector: '.dbd-a-card-container',
      metricSelector: '.overlay-card-content-main-num',
      nameSelector: '.overlay-card-metric-title .overlay-card-text-name',
      defaultName: '指标卡片',
      businessData: { type: 'dashboard-metric-card', module: 'dashboard' },
      pathPattern: ['/overview/']
    },
    // mobile/pc 实时活跃
    {
      containerSelector: '.dbd-overlay-card',
      metricSelector: '.overlay-card-content .cart-margin-num',
      nameSelector: '.overlay-card-metric-title .overlay-card-text-name',
      defaultName: '指标卡片',
      businessData: { type: 'dashboard-metric-card', module: 'dashboard' },
      pathPattern: ['/realtime/']
    },
    // 舆情核心指标卡片
    {
      containerSelector: '.metrics-list-wrapper .dbo-rank-card',
      metricSelector: '.dbo-rank-card-bd .dbo-compare-num',
      nameSelector: '.dbo-rank-card-hd .dbo-rank-card-title',
      defaultName: '舆情指标卡片',
      businessData: { type: 'opinion-metric-card', module: 'opinion' },
      pathPattern: ['/opinion/']
    },
  ]
};

/** 空状态类名列表 */
export const SIMPLE_EMPTY_CLASS_NAMES = [
  'no-data',
  'empty-state', 
  'dbo-no-data',
  'empty-content',
  'no-content',
  'data-empty',
  'metrics-empty'
];

/** 业务数据属性前缀 */
export const SIMPLE_BUSINESS_DATA_PREFIXES = [
  'data-game-code',
  'data-time-range',
  'data-chart-type',
  'data-metrics-type'
] as const; 